import { matchPath } from 'react-router-dom'

/**
 * Route → tab title map, the React equivalent of each Angular component calling
 * `this.appService.title('Dashboard')`. Titles follow the label the panel itself
 * shows (sidebar / page header), falling back to the Angular title text.
 *
 * `/reports/*` list pages are not listed here — their titles come from the
 * reports hub labels (see `getReportPageTitle`).
 */

/** Paths without route params — matched by direct lookup. */
const STATIC_PAGE_TITLES: Record<string, string> = {
  // Auth / public
  '/login': 'Login',
  '/auth/login': 'Login',
  '/forgot-password': 'Forgot Password',
  '/forgotpassword': 'Forgot Password',
  '/auth/forgotpassword': 'Forgot Password',
  '/apply-now': 'Apply Now',
  '/apply-now/invite-code-question': 'Apply Now',
  '/apply-now/enter-invite-code': 'Enter Invite Code',
  '/apply-now/verify-info': 'Verify Your Information',
  '/apply-now/need-to-apply-first': 'Apply Now',

  // Standalone documents / notices
  '/gdpr-consent': 'Service Agreement',
  '/resident-application/terms_info/fair_housing_act': 'Fair Housing Act',
  '/resident-application/terms_info/authorization_for_liberty':
    'Authorization for Liberty Rent',
  '/applicant/application/terms_info/fair_housing_act': 'Fair Housing Act',
  '/applicant/application/terms_info/authorization_for_liberty':
    'Authorization for Liberty Rent',

  // Admin shell
  '/dashboard': 'Dashboard',
  '/help-about': 'Help / About',
  '/user/profile': 'Profile',
  '/invite': 'Invites',
  '/invite/add': 'Add Invite',
  '/yardiinvite': 'Yardi Invites',
  '/yardiinvite/search': 'Yardi Guest Search',
  '/company': 'Companies',
  '/company/add': 'Add Company',
  '/property': 'Properties',
  '/property/add': 'Add Property',
  '/product': 'Products',
  '/product/add': 'Add Product',
  '/model': 'Models',
  '/model/add': 'Add Model',
  '/user-management': 'User Management',
  '/user-management/add': 'Add User',
  '/application': 'Applications',
  '/resident': 'Residents',
  '/defaults': 'Reimbursements',
  '/reports': 'All Reports',
  '/reports-scheduler': 'Reports Scheduler',
  '/imap-logs': 'IMAP Inbox',
  '/underwriting-config': 'Underwriting Config',
  '/impact-notes': 'Impact Notes Updates',
  '/report-description': 'Report Description',
  '/version': "What's New",
  '/jobs/queued': 'Queued Jobs',
  '/jobs/failed': 'Failed Jobs',
  '/jobs/cron': 'Cron Jobs',
  '/jobs/cron-monitor': 'Cron Monitor',
  '/jobs/cron-monitor/list': 'Cron Monitor Jobs',
  '/notification-settings': 'Notification Settings',
  '/policy': 'Policy List',
  '/stock': 'Stock Registry',
  '/terms-and-conditions': 'Terms & Conditions',
  '/gdpr-consent/gdpr-document': 'Service Agreement',
  '/gdpr-consent/add': 'Add Service Agreement',
  '/organizations': 'Organizations',
  '/organizations/add': 'Add Organization',
  '/system-announcement': 'System Announcement',
  '/dashboard-tiles': 'Dashboard Tiles',
  '/mailtrap': 'Mailtrap Logs',
  '/email-templates': 'Email Templates',
  '/email-templates/add': 'Add Email Template',
  '/user-roles': 'Roles',
  '/property-maintenance': 'Property Maintenance',
  '/transfer-list': 'Transfer List',
  '/import-data': 'Import Data',
  '/sales-team': 'Teams',
  '/config': 'Config',
  '/screening-info-requests': 'Screening Info Requests',
  '/frequently-used-notes': 'Reimbursement Internal Notes',

  // Applicant shell
  '/applicant': 'Dashboard',
  '/applicant/dashboard': 'Dashboard',
  '/applicant/messages': 'Messages',
  '/applicant/application/about': 'About Yourself',
  '/applicant/application/address': 'Address',
  '/applicant/application/property': 'Property',
  '/applicant/application/income': 'Income and Employment',
  '/applicant/application/history': 'Personal History',
  '/applicant/application/signature': 'Notices / Signature',
  '/applicant/application/payment': 'Payment',
  '/applicant/help': 'Help / About',
  '/applicant/account': 'Profile',
}

/** Paths with route params — matched in order, most specific first. */
const DYNAMIC_PAGE_TITLES: ReadonlyArray<{ pattern: string; title: string }> = [
  // Auth / public
  { pattern: '/resetPassword/:resetToken', title: 'Reset Password' },
  { pattern: '/auth/resetPassword/:resetToken', title: 'Reset Password' },
  { pattern: '/apply/:inviteId', title: 'Apply' },
  { pattern: '/co-applicant/apply/:inviteId', title: 'Apply' },

  // Standalone documents / notices
  {
    pattern: '/explanation-service-aggrement/:id',
    title: 'Explanation of Reimbursement Process',
  },
  {
    pattern: '/resident-application/terms_info/credit_reporting_bureau/:applicantId?',
    title: 'Credit Reporting Bureau',
  },
  {
    pattern: '/applicant/application/terms_info/credit_reporting_bureau/:applicantId?',
    title: 'Credit Reporting Bureau',
  },
  { pattern: '/resident-application/:applicantId', title: 'Resident Application' },

  // Report detail pages (list pages come from the reports hub labels)
  { pattern: '/reports/payment-logs/detail/:logId', title: 'Transaction Details' },
  { pattern: '/reports/payment-logs/:logId', title: 'Transaction Details' },
  {
    pattern: '/reports/default-payment-logs/edit/:logId',
    title: 'Transaction Details Edit',
  },
  { pattern: '/reports/default-payment-logs/:logId', title: 'Transaction Details' },
  {
    pattern: '/reports/chargeback/detail/:applicantId/:logId',
    title: 'Chargeback Details',
  },
  { pattern: '/reports/mail-logs/read/:logId', title: 'Mail Detail' },
  { pattern: '/reports/mailgun-logs/read/:logId', title: 'Mail Detail' },
  { pattern: '/imap-logs/read/:logId', title: 'Mail Detail' },

  // Admin shell
  { pattern: '/invite/edit/:code', title: 'Edit Invite' },
  { pattern: '/company/edit/:id', title: 'Edit Company' },
  { pattern: '/property/edit/:id', title: 'Edit Property' },
  { pattern: '/product/edit/:productId', title: 'Edit Product' },
  { pattern: '/model/edit/:modelId', title: 'Edit Model' },
  { pattern: '/user-management/edit/:userId', title: 'Edit User' },
  { pattern: '/user-management/roles/:userId', title: 'Assign User Role' },
  { pattern: '/application/:applicantId', title: 'Application Detail' },
  { pattern: '/resident/:pagetype/:applicantId/:userId', title: 'Resident Detail' },
  { pattern: '/defaults/:pagetype/:applicantId/:userId', title: 'Reimbursement Detail' },
  { pattern: '/transfer/:id/:companyId', title: 'Transfer Process' },
  { pattern: '/transfer/:id', title: 'Transfer Process' },
  { pattern: '/terms-and-conditions/edit/:policyId', title: 'Edit Terms & Conditions' },
  { pattern: '/gdpr-consent/edit/:documentId', title: 'Edit Service Agreement' },
  { pattern: '/gdpr-consent/view/:documentId', title: 'Service Agreement' },
  { pattern: '/organizations/edit/:orgId', title: 'Edit Organization' },
  { pattern: '/email-templates/edit/:templateId', title: 'Edit Email Template' },
  { pattern: '/jobs/cron-monitor/:kind', title: 'Cron Monitor Jobs' },
]

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '')
  }
  return pathname
}

/** Title for `pathname`, or `null` when no route entry matches. */
export function resolvePageTitle(pathname: string): string | null {
  const path = normalizePathname(pathname)
  const staticTitle = STATIC_PAGE_TITLES[path]
  if (staticTitle) return staticTitle

  for (const { pattern, title } of DYNAMIC_PAGE_TITLES) {
    if (matchPath(pattern, path)) return title
  }
  return null
}
